// ─────────────────────────────────────────────────────────────────
// Control Systems — Linearization
// Matches the style/structure of ControlSystems.jsx and SystemModeling.jsx
// ─────────────────────────────────────────────────────────────────

const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 1 — What Linearization Does: Geometric Intuition
// ─────────────────────────────────────────────────────────────────
function DiagramGeometricIntuition() {
  const W = 900, H = 380;

  // Three nonlinear functions plotted on separate mini-axes
  // Left panel: f(x) = x^3 - x + 0.5  with tangent at x0=0.8
  // Middle panel: f(x) = sin(x) with tangent at x0 = 0.7
  // Right panel: f(x) = e^(0.6x) with tangent at x0 = 1.0

  const panels = [
    {
      title: 'Cubic: f(x) = x³ − x + 0.5',
      x0: 0.8,
      fn:  x => x*x*x - x + 0.5,
      dfn: x => 3*x*x - 1,
      xMin: -1.4, xMax: 1.6,
      yMin: -1.2, yMax: 2.0,
      color: '#c0392b',
    },
    {
      title: 'Trigonometric: f(x) = sin(x)',
      x0: 0.9,
      fn:  x => Math.sin(x),
      dfn: x => Math.cos(x),
      xMin: -1.2, xMax: 2.6,
      yMin: -1.3, yMax: 1.3,
      color: '#2E75B6',
    },
    {
      title: 'Exponential: f(x) = e^(0.6x)',
      x0: 1.0,
      fn:  x => Math.exp(0.6*x),
      dfn: x => 0.6*Math.exp(0.6*x),
      xMin: -1.0, xMax: 3.0,
      yMin: -0.2, yMax: 3.8,
      color: '#27ae60',
    },
  ];

  const PW = 260, PH = 260, PAD = 40;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Linearization — Geometric Interpretation: Tangent-Line Approximation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          {panels.map((p,pi) => (
            <marker key={pi} id={`gi-arr-${pi}`} markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
              <polygon points="0 0,6 3,0 6" fill={p.color}/>
            </marker>
          ))}
          <marker id="gi-blk" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#555"/>
          </marker>
        </defs>

        {panels.map((pan, pi) => {
          const ox = 20 + pi * (PW + 30);   // panel x origin
          const oy = 50;                      // panel y origin
          const { fn, dfn, x0, xMin, xMax, yMin, yMax, color, title } = pan;

          const sx = x => ox + PAD + (x - xMin) / (xMax - xMin) * (PW - 2*PAD);
          const sy = y => oy + PH - PAD - (y - yMin) / (yMax - yMin) * (PH - 2*PAD);

          // Nonlinear curve points
          const N = 80;
          const curvePts = [];
          for (let i = 0; i <= N; i++) {
            const x = xMin + i * (xMax - xMin) / N;
            curvePts.push([sx(x), sy(fn(x))]);
          }
          const curveD = curvePts.map(([x,y],i) => `${i===0?'M':'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

          // Tangent line
          const y0 = fn(x0);
          const slope = dfn(x0);
          const tx1 = xMin + 0.05*(xMax-xMin);
          const tx2 = xMax - 0.05*(xMax-xMin);
          const tanD = `M ${sx(tx1).toFixed(1)},${sy(y0 + slope*(tx1-x0)).toFixed(1)} L ${sx(tx2).toFixed(1)},${sy(y0 + slope*(tx2-x0)).toFixed(1)}`;

          // Valid region shading (±0.5 around x0)
          const vHalfW = (xMax-xMin)*0.18;
          const vx1 = Math.max(xMin, x0 - vHalfW);
          const vx2 = Math.min(xMax, x0 + vHalfW);

          // Panel background
          return (
            <g key={pi}>
              <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
              <text x={ox + PW/2} y={oy - 8} textAnchor="middle" fontSize={10.5} fill="#7B0D0D" fontWeight="700">{title}</text>

              {/* Valid region */}
              <rect
                x={sx(vx1)} y={oy + PAD - 5}
                width={sx(vx2) - sx(vx1)} height={PH - 2*PAD + 5}
                fill={color} opacity={0.07}
              />

              {/* Axes */}
              <line x1={ox+PAD} y1={oy+PH-PAD} x2={ox+PW-PAD+8} y2={oy+PH-PAD} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#gi-blk)"/>
              <line x1={ox+PAD} y1={oy+PH-PAD} x2={ox+PAD} y2={oy+PAD-8} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#gi-blk)"/>

              {/* Zero lines */}
              {yMin < 0 && yMax > 0 && (
                <line x1={ox+PAD} y1={sy(0)} x2={ox+PW-PAD} y2={sy(0)} stroke="#ddd" strokeWidth="1"/>
              )}
              {xMin < 0 && xMax > 0 && (
                <line x1={sx(0)} y1={oy+PAD} x2={sx(0)} y2={oy+PH-PAD} stroke="#ddd" strokeWidth="1"/>
              )}

              {/* Nonlinear curve */}
              <path d={curveD} fill="none" stroke={color} strokeWidth="2.5" opacity="0.9"/>

              {/* Tangent (linearised) line */}
              <path d={tanD} fill="none" stroke="#e07b00" strokeWidth="1.8" strokeDasharray="7,4"/>

              {/* Operating point */}
              <circle cx={sx(x0)} cy={sy(y0)} r={6} fill="#e07b00" stroke="#fff" strokeWidth="2"/>

              {/* Dashed drop lines */}
              <line x1={sx(x0)} y1={sy(y0)} x2={sx(x0)} y2={oy+PH-PAD} stroke="#e07b00" strokeWidth="1" strokeDasharray="3,3"/>
              <line x1={sx(x0)} y1={sy(y0)} x2={ox+PAD}  y2={sy(y0)}   stroke="#e07b00" strokeWidth="1" strokeDasharray="3,3"/>

              {/* Labels */}
              <text x={sx(x0)+7}  y={sy(y0)-10} fontSize={9.5} fill="#e07b00" fontWeight="700">OP (x₀,f(x₀))</text>
              <text x={ox+PW-PAD+10} y={oy+PH-PAD+4} fontSize={9} fill="#555">x</text>
              <text x={ox+PAD-4} y={oy+PAD-12}      fontSize={9} fill="#555">f</text>

              {/* Legend inside panel bottom */}
              <rect x={ox+PAD-2} y={oy+PH-PAD+12} width={PW-2*PAD+4} height={28} rx={2} fill="#fffcf5"/>
              <line x1={ox+PAD+4} y1={oy+PH-PAD+24} x2={ox+PAD+28} y2={oy+PH-PAD+24} stroke={color} strokeWidth="2.5"/>
              <text x={ox+PAD+32} y={oy+PH-PAD+28} fontSize={9} fill={color}>Nonlinear f(x)</text>
              <line x1={ox+PAD+4} y1={oy+PH-PAD+34} x2={ox+PAD+28} y2={oy+PH-PAD+34} stroke="#e07b00" strokeWidth="1.8" strokeDasharray="5,3"/>
              <text x={ox+PAD+32} y={oy+PH-PAD+38} fontSize={9} fill="#e07b00">Linearised f(x₀)+f′Δx</text>
            </g>
          );
        })}

        {/* Bottom annotation row */}
        <rect x={10} y={340} width={880} height={32} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={450} y={353} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">
          Key insight: The linearised model (orange dashed tangent) is a good approximation only in the shaded region near the operating point.
        </text>
        <text x={450} y={368} textAnchor="middle" fontSize={10.5} fill="#555">
          Further from the operating point, the nonlinear behaviour diverges — the linear model breaks down and a different (or gain-scheduled) model is needed.
        </text>
      </svg>
      <div style={S.dCaption}>Linearization replaces a nonlinear function with its tangent line at the chosen operating point. For a cubic, sine, and exponential function, the orange tangent line (y = f(x₀) + f′(x₀)·Δx) matches the true curve closely near x₀ but diverges as the perturbation Δx grows. The shaded band marks the region where the linear approximation is valid.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 2 — Taylor Series Step-by-Step
// ─────────────────────────────────────────────────────────────────
function DiagramTaylorSeries() {
  const W = 900, H = 360;

  // Show f(x)=sin(x) + successive Taylor approximations around x0=0
  // T1(x) = x,  T3 = x - x³/6,  T5 = x - x³/6 + x⁵/120
  const xMin = -3.5, xMax = 3.5, yMin = -1.6, yMax = 1.6;
  const PW = 420, PH = 280;
  const ox = 20, oy = 40;
  const sx = x => ox + 30 + (x - xMin)/(xMax-xMin)*(PW-60);
  const sy = y => oy + PH - 30 - (y - yMin)/(yMax-yMin)*(PH-60);

  const N = 120;
  const mkPath = fn => {
    let d = '';
    for (let i=0; i<=N; i++) {
      const x = xMin + i*(xMax-xMin)/N;
      const y = fn(x);
      if (Math.abs(y) > 2.5) { d += ' '; continue; } // clip
      d += `${i===0?'M':'L'}${sx(x).toFixed(1)},${sy(y).toFixed(1)} `;
    }
    return d.trim();
  };

  const sinPath = mkPath(x => Math.sin(x));
  const t1Path  = mkPath(x => x);
  const t3Path  = mkPath(x => x - x**3/6);
  const t5Path  = mkPath(x => x - x**3/6 + x**5/120);

  // Term-by-term data — declared here (outside JSX) to avoid syntax error
  const termData = [
    { order:'0th', val:'f(0)=0',          term:'0',         color:'#aaa',    formula:'Constant — just the value at x\u2080' },
    { order:'1st', val:'f\u2032(0)=1',    term:'+ x',       color:'#e07b00', formula:'Linear term — slope at x\u2080  (LINEARIZATION stops here)' },
    { order:'2nd', val:'f\u2033(0)=0',    term:'+ 0\u00b7x\u00b2/2!', color:'#bbb', formula:'Quadratic term — zero for sin at x\u2080=0' },
    { order:'3rd', val:'f\u2034(0)=\u22121', term:'\u2212 x\u00b3/6', color:'#9b59b6', formula:'Cubic correction — improves fit for |x|>0.5' },
    { order:'5th', val:'f\u2075(0)=1',    term:'+ x\u2075/120', color:'#2E75B6', formula:'5th-order — nearly perfect for |x| < \u03c0/2' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Taylor Series Expansion — Successive Approximations of sin(x) Around x₀ = 0</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <clipPath id="ts-clip">
            <rect x={ox+28} y={oy} width={PW-56} height={PH-28}/>
          </clipPath>
        </defs>

        {/* Main plot area */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>

        {/* Grid */}
        {[-3,-2,-1,1,2,3].map(v => (
          <g key={v}>
            <line x1={sx(v)} y1={oy+10} x2={sx(v)} y2={oy+PH-28} stroke="#f0cece" strokeWidth="1" clipPath="url(#ts-clip)"/>
            <text x={sx(v)} y={oy+PH-14} textAnchor="middle" fontSize={9} fill="#aaa">{v}</text>
          </g>
        ))}
        {[-1,-0.5,0.5,1].map(v => (
          <line key={v} x1={ox+30} y1={sy(v)} x2={ox+PW-28} y2={sy(v)} stroke="#f0cece" strokeWidth="1"/>
        ))}

        {/* Axes */}
        <line x1={ox+30} y1={sy(0)} x2={ox+PW-24} y2={sy(0)} stroke="#aaa" strokeWidth="1.5"/>
        <line x1={sx(0)} y1={oy+10} x2={sx(0)} y2={oy+PH-26} stroke="#aaa" strokeWidth="1.5"/>

        {/* Curves */}
        <path d={t1Path}  fill="none" stroke="#e07b00" strokeWidth="1.5" strokeDasharray="8,4" clipPath="url(#ts-clip)"/>
        <path d={t3Path}  fill="none" stroke="#9b59b6" strokeWidth="1.8" strokeDasharray="5,3" clipPath="url(#ts-clip)"/>
        <path d={t5Path}  fill="none" stroke="#2E75B6" strokeWidth="2"   strokeDasharray="4,2" clipPath="url(#ts-clip)"/>
        <path d={sinPath} fill="none" stroke="#c0392b" strokeWidth="2.5" clipPath="url(#ts-clip)"/>

        {/* Operating point */}
        <circle cx={sx(0)} cy={sy(0)} r={6} fill="#e07b00" stroke="#fff" strokeWidth="2"/>
        <text x={sx(0)+8} y={sy(0)-8} fontSize={9.5} fill="#e07b00" fontWeight="700">x₀=0</text>

        {/* Labels on curves at right edge */}
        <text x={ox+PW-24} y={sy(xMax)} fontSize={10} fill="#c0392b" fontWeight="700">sin(x)</text>
        <text x={ox+PW-24} y={sy(xMax)+1} fontSize={9} fill="#c0392b" dy="12">true</text>

        {/* x label */}
        <text x={ox+PW-20} y={sy(0)+4} fontSize={9} fill="#aaa">x</text>

        {/* RIGHT PANEL: Taylor expansion formula breakdown */}
        <rect x={460} y={oy} width={430} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={675} y={oy+20} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Taylor Series Formula</text>

        {/* General formula */}
        <rect x={470} y={oy+28} width={410} height={42} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={675} y={oy+44} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontFamily="'Courier New',monospace">f(x) = f(x₀) + f′(x₀)(x−x₀) + f″(x₀)(x−x₀)²/2!</text>
        <text x={675} y={oy+61} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontFamily="'Courier New',monospace">         + f‴(x₀)(x−x₀)³/3! + ···</text>

        {/* Term-by-term breakdown for sin(x) at x0=0 */}
        <text x={470} y={oy+88} fontSize={11} fill="#333" fontWeight="700">For f(x) = sin(x) at x&#x2080; = 0:</text>

        {termData.map(({order,val,term,color,formula},i) => (
          <g key={i}>
            <rect x={468} y={oy+96+i*34} width={412} height={32} rx={2}
              fill={color==='#aaa'||color==='#bbb'?'#f9f9f9':i%2===0?'#fff5f5':'#fff'}
              stroke={color==='#aaa'||color==='#bbb'?'#ddd':color} strokeWidth="0.8"/>
            <text x={474} y={oy+108+i*34} fontSize={9.5} fill="#7B0D0D" fontWeight="700">{order}</text>
            <text x={520} y={oy+108+i*34} fontSize={9}   fill="#555" fontFamily="'Courier New',monospace">{val}</text>
            <text x={680} y={oy+108+i*34} fontSize={10}  fill={color} fontWeight="700" fontFamily="'Courier New',monospace">{term}</text>
            <text x={474} y={oy+120+i*34} fontSize={9}   fill="#555">{formula}</text>
          </g>
        ))}

        {/* Linearization highlight box */}
        <rect x={468} y={oy+130} width={412} height={14} rx={1} fill="#e07b00" opacity={0.15}/>
        <line x1={468} y1={oy+130} x2={880} y2={oy+130} stroke="#e07b00" strokeWidth="1.5"/>
        <line x1={468} y1={oy+144} x2={880} y2={oy+144} stroke="#e07b00" strokeWidth="1.5"/>
        <text x={675} y={oy+141} textAnchor="middle" fontSize={9} fill="#e07b00" fontWeight="700">← Linearization truncates HERE (keeps only 1st-order term)</text>

        {/* Legend for curves */}
        <rect x={468} y={oy+245} width={412} height={28} rx={3} fill="#fffcf5" stroke="#f0cece"/>
        {[
          { c:'#c0392b', d:'none',   lbl:'True sin(x)' },
          { c:'#e07b00', d:'8,4',    lbl:'T₁: x  (linear approx.)' },
          { c:'#9b59b6', d:'5,3',    lbl:'T₃: x − x³/6' },
          { c:'#2E75B6', d:'4,2',    lbl:'T₅: x − x³/6 + x⁵/120' },
        ].map(({c,d,lbl},i) => (
          <g key={i}>
            <line x1={474+i*104} y1={oy+259} x2={498+i*104} y2={oy+259} stroke={c} strokeWidth={i===0?2.5:1.8} strokeDasharray={d==='none'?undefined:d}/>
            <text x={501+i*104} y={oy+263} fontSize={8.5} fill={c}>{lbl}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The Taylor series of sin(x) around x₀=0 builds in accuracy with each additional term. Linearization keeps only the first-order term (orange, T₁=x), which matches sin(x) well for |x| &lt; 0.3 rad but drifts notably by |x| = 1. Adding the cubic term (T₃, purple) gives excellent accuracy to |x| ≈ 1.5. The key control systems insight: we only need T₁ if we guarantee small perturbations.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 3 — Multivariable Jacobian Linearization
// ─────────────────────────────────────────────────────────────────
function DiagramJacobian() {
  const W = 900, H = 390;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Multivariable Linearization — Jacobian Matrices &amp; State-Space Derivation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: Step-by-step procedure ── */}
        <rect x={10} y={10} width={430} height={370} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={225} y={30} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Step-by-Step Linearization Procedure</text>

        {[
          {
            step:'① Write nonlinear state equations',
            lines:[
              'ẋ₁ = f₁(x₁, x₂, …, xₙ, u₁, …, uₘ)',
              'ẋ₂ = f₂(x₁, x₂, …, xₙ, u₁, …, uₘ)',
              '  ⋮',
              'ẋₙ = fₙ(x₁, x₂, …, xₙ, u₁, …, uₘ)',
            ],
            fill:'#fff5f5', bc:'#c0392b', y:38,
          },
          {
            step:'② Find equilibrium: set all ẋᵢ = 0',
            lines:[
              'Solve: fᵢ(x₀, u₀) = 0  for all i',
              'May have multiple solutions (equilibria)',
              'Choose operating point of interest',
            ],
            fill:'#eaf3ff', bc:'#2E75B6', y:128,
          },
          {
            step:'③ Define perturbation variables',
            lines:[
              'Δx = x − x₀   (deviation from equilibrium)',
              'Δu = u − u₀   (deviation from nominal input)',
              'Δy = y − y₀   (deviation from nominal output)',
            ],
            fill:'#f0fff4', bc:'#27ae60', y:208,
          },
          {
            step:'④ Compute Jacobian matrices',
            lines:[
              'A = ∂f/∂x |₍ₓ₀,ᵤ₀₎   [n×n] system matrix',
              'B = ∂f/∂u |₍ₓ₀,ᵤ₀₎   [n×m] input matrix',
              'C = ∂g/∂x |₍ₓ₀,ᵤ₀₎   [p×n] output matrix',
              'D = ∂g/∂u |₍ₓ₀,ᵤ₀₎   [p×m] feedthrough',
            ],
            fill:'#fff5e6', bc:'#e07b00', y:282,
          },
        ].map(({ step, lines, fill, bc, y }) => (
          <g key={y}>
            <rect x={18} y={y} width={414} height={lines.length*18+28} rx={3} fill={fill} stroke={bc} strokeWidth="1.2"/>
            <text x={26} y={y+17} fontSize={11} fill="#7B0D0D" fontWeight="700">{step}</text>
            {lines.map((l, i) => (
              <text key={i} x={26} y={y+33+i*18} fontSize={10} fill="#333" fontFamily="'Courier New',monospace">{l}</text>
            ))}
          </g>
        ))}

        {/* Final model box */}
        <rect x={18} y={360} width={414} height={14} rx={2} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.2"/>
        <text x={225} y={371} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">⑤ Linearised model:  Δẋ = A·Δx + B·Δu ,  Δy = C·Δx + D·Δu</text>

        {/* ── RIGHT: Jacobian matrix visual ── */}
        <rect x={455} y={10} width={435} height={370} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={672} y={30} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Jacobian Matrix — Element Meaning</text>

        {/* A matrix illustration */}
        <text x={465} y={55} fontSize={11} fill="#7B0D0D" fontWeight="700">A = ∂f/∂x  (n×n System Matrix):</text>
        <rect x={463} y={62} width={417} height={118} rx={4} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>

        {/* Matrix bracket visual */}
        {/* Left bracket */}
        <text x={470} y={98} fontSize={50} fill="#e07b00" fontWeight="300">⎡</text>
        <text x={470} y={138} fontSize={50} fill="#e07b00" fontWeight="300">⎣</text>
        {/* Right bracket */}
        <text x={550} y={98} fontSize={50} fill="#e07b00" fontWeight="300">⎤</text>
        <text x={550} y={138} fontSize={50} fill="#e07b00" fontWeight="300">⎦</text>

        {/* Matrix entries */}
        {[
          ['∂f₁/∂x₁', '∂f₁/∂x₂', '⋯', '∂f₁/∂xₙ'],
          ['∂f₂/∂x₁', '∂f₂/∂x₂', '⋯', '∂f₂/∂xₙ'],
          ['  ⋮',       '  ⋮',      '⋱',  '  ⋮'     ],
          ['∂fₙ/∂x₁', '∂fₙ/∂x₂', '⋯', '∂fₙ/∂xₙ'],
        ].map((row, ri) => row.map((cell, ci) => (
          <text key={`${ri}-${ci}`}
            x={490 + ci*70} y={85 + ri*18}
            fontSize={9} fill={ri===ci?'#c0392b':'#333'}
            fontFamily="'Courier New',monospace"
            fontWeight={ri===ci?'700':'400'}>{cell}</text>
        )))}

        {/* Diagonal highlight note */}
        <text x={670} y={82} fontSize={9.5} fill="#c0392b" fontWeight="700">Diagonal: self-feedback</text>
        <text x={670} y={96} fontSize={9}   fill="#555">how state xᵢ affects its own rate ẋᵢ</text>
        <text x={670} y={113} fontSize={9.5} fill="#333" fontWeight="700">Off-diagonal: coupling</text>
        <text x={670} y={127} fontSize={9}   fill="#555">how xⱼ affects ẋᵢ (cross terms)</text>
        <text x={670} y={144} fontSize={9}   fill="#555">All evaluated at (x₀, u₀)</text>
        <text x={670} y={160} fontSize={9}   fill="#9b59b6" fontWeight="700">Eigenvalues of A = closed-loop poles</text>
        <text x={670} y={174} fontSize={9}   fill="#9b59b6">Stability determined by sign of Re(λᵢ)</text>

        {/* B matrix */}
        <text x={465} y={200} fontSize={11} fill="#7B0D0D" fontWeight="700">B = ∂f/∂u  (n×m Input Matrix):</text>
        <rect x={463} y={207} width={417} height={60} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={473} y={222} fontSize={10} fill="#2E75B6" fontFamily="'Courier New',monospace">Aᵢⱼ = ∂fᵢ/∂xⱼ |₍ₓ₀,ᵤ₀₎ — rate of change of ith state eqn wrt jth state</text>
        <text x={473} y={238} fontSize={10} fill="#2E75B6" fontFamily="'Courier New',monospace">Bᵢⱼ = ∂fᵢ/∂uⱼ |₍ₓ₀,ᵤ₀₎ — how jth input drives ith state equation</text>
        <text x={473} y={254} fontSize={10} fill="#2E75B6" fontFamily="'Courier New',monospace">Cᵢⱼ = ∂gᵢ/∂xⱼ |₍ₓ₀,ᵤ₀₎ — how jth state appears in ith output</text>
        <text x={473} y={262} fontSize={9}  fill="#555">Jacobians are evaluated once at the operating point and remain constant.</text>

        {/* Key properties */}
        <text x={465} y={285} fontSize={11} fill="#7B0D0D" fontWeight="700">Key Properties of the Linearised Model:</text>
        {[
          { c:'#c0392b', t:'Valid only locally — small perturbations Δx, Δu near (x₀, u₀)' },
          { c:'#2E75B6', t:'Different operating point → different A, B, C, D matrices' },
          { c:'#27ae60', t:'Linear superposition applies to the Δ variables' },
          { c:'#e07b00', t:'Laplace transform valid → transfer function G(s) = C(sI−A)⁻¹B + D' },
          { c:'#9b59b6', t:'Stability of linearised model ≠ global stability of nonlinear system' },
          { c:'#555',    t:'Higher-order terms (Δx², Δx³, …) captured by nonlinear remainder' },
        ].map(({ c, t }, i) => (
          <g key={i}>
            <rect x={463} y={292+i*16} width={417} height={15} rx={1} fill={i%2===0?'#fafafa':'#fff'}/>
            <text x={469} y={303+i*16} fontSize={9.5} fill={c}>▸ {t}</text>
          </g>
        ))}

        {/* Stability note box */}
        <rect x={463} y={392} width={417} height={0} rx={2}/>
      </svg>
      <div style={S.dCaption}>Multivariable linearization computes four Jacobian matrices (A, B, C, D) by evaluating partial derivatives of the nonlinear functions at the chosen equilibrium. The resulting linear model Δẋ = AΔx + BΔu is exact in the limit of infinitesimally small perturbations, and increasingly approximate as perturbations grow. The eigenvalues of A determine local stability of the nonlinear system at that equilibrium.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 4 — Equilibrium Points & Phase Portraits
// ─────────────────────────────────────────────────────────────────
function DiagramEquilibrium() {
  const W = 900, H = 370;

  // Phase portrait of ẋ₁=x₂, ẋ₂= -sin(x₁) - 0.3x₂  (damped pendulum)
  // Draw vector field + representative trajectories
  const PW = 420, PH = 300;
  const ox = 20, oy = 38;
  const xMin = -4, xMax = 4;
  const yMin = -3, yMax = 3;
  const sx = x => ox + 30 + (x-xMin)/(xMax-xMin)*(PW-60);
  const sy = y => oy + PH - 20 - (y-yMin)/(yMax-yMin)*(PH-40);

  // Vector field arrows
  const vfGrid = [];
  for (let xi = -3.5; xi <= 3.5; xi += 0.8) {
    for (let yi = -2.5; yi <= 2.5; yi += 0.75) {
      const dx = yi;
      const dy = -Math.sin(xi) - 0.3*yi;
      const mag = Math.sqrt(dx*dx + dy*dy);
      const scale = 0.22;
      vfGrid.push({ xi, yi, dx: dx/mag*scale, dy: dy/mag*scale, mag });
    }
  }

  // Trajectory integration (Euler)
  const integrate = (x0, y0, steps, dt) => {
    const pts = [[x0,y0]];
    let [x, y] = [x0, y0];
    for (let i=0; i<steps; i++) {
      x += dt * y;
      y += dt * (-Math.sin(x) - 0.3*y);
      if (Math.abs(x) > 4.5 || Math.abs(y) > 3.5) break;
      pts.push([x, y]);
    }
    return pts;
  };

  const trajs = [
    { x0: 0.5,  y0: 2.0,  color:'#2E75B6', steps:400, dt:0.04 },
    { x0:-0.5,  y0:-1.5,  color:'#2E75B6', steps:400, dt:0.04 },
    { x0: 2.0,  y0: 1.0,  color:'#27ae60', steps:400, dt:0.04 },
    { x0:-2.0,  y0:-1.0,  color:'#27ae60', steps:400, dt:0.04 },
    { x0: 3.2,  y0: 0.5,  color:'#9b59b6', steps:500, dt:0.04 },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Equilibrium Points — Phase Portrait of a Damped Pendulum (ẋ₂ = −sin x₁ − 0.3x₂)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <clipPath id="pp-clip">
            <rect x={ox+28} y={oy} width={PW-56} height={PH-18}/>
          </clipPath>
          <marker id="vf-arr" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
            <polygon points="0 0,5 2.5,0 5" fill="#ddd"/>
          </marker>
        </defs>

        {/* Phase portrait panel */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fafcff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Phase Portrait (x₁=θ, x₂=θ̇)</text>

        {/* Axes */}
        <line x1={ox+28} y1={sy(0)} x2={ox+PW-26} y2={sy(0)} stroke="#bbb" strokeWidth="1.2"/>
        <line x1={sx(0)} y1={oy+8} x2={sx(0)} y2={oy+PH-18} stroke="#bbb" strokeWidth="1.2"/>
        {[-3,-2,-1,1,2,3].map(v=>(
          <g key={v}>
            <text x={sx(v)} y={sy(0)+14} textAnchor="middle" fontSize={8} fill="#aaa">{v}</text>
            <text x={sx(0)-6} y={sy(v)+3} textAnchor="end" fontSize={8} fill="#aaa">{v}</text>
          </g>
        ))}
        <text x={ox+PW-22} y={sy(0)+4} fontSize={9} fill="#aaa">x₁</text>
        <text x={sx(0)+4} y={oy+10} fontSize={9} fill="#aaa">x₂</text>

        {/* Vector field */}
        {vfGrid.map(({ xi, yi, dx, dy, mag }, i) => {
          const x1 = sx(xi), y1 = sy(yi);
          const x2 = sx(xi+dx), y2 = sy(yi-dy);
          const alpha = Math.min(0.9, 0.3 + mag*0.4);
          return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="#c8d8e8" strokeWidth="1"
              markerEnd="url(#vf-arr)"
              opacity={alpha}
              clipPath="url(#pp-clip)"/>
          );
        })}

        {/* Trajectories */}
        {trajs.map(({ x0, y0, color, steps, dt }, ti) => {
          const pts = integrate(x0, y0, steps, dt);
          const d = pts.map(([x,y],i) => `${i===0?'M':'L'}${sx(x).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');
          return <path key={ti} d={d} fill="none" stroke={color} strokeWidth="1.8" opacity="0.75" clipPath="url(#pp-clip)"/>;
        })}

        {/* Stable equilibrium at (0,0) */}
        <circle cx={sx(0)} cy={sy(0)} r={7} fill="#27ae60" stroke="#fff" strokeWidth="2"/>
        <text x={sx(0)+10} y={sy(0)-8} fontSize={9.5} fill="#27ae60" fontWeight="700">Stable EP (0,0)</text>
        <text x={sx(0)+10} y={sy(0)+4} fontSize={8.5} fill="#555">sink — spirals inward</text>

        {/* Unstable equilibrium at (±π, 0) ≈ ±3.14 */}
        {[-Math.PI, Math.PI].map((xeq, i) => (
          <g key={i}>
            <rect x={sx(xeq)-6} y={sy(0)-6} width={12} height={12} fill="#c0392b" stroke="#fff" strokeWidth="2" transform={`rotate(45,${sx(xeq)},${sy(0)})`}/>
            <text x={sx(xeq)} y={sy(0)-14} textAnchor="middle" fontSize={9} fill="#c0392b" fontWeight="700">Unstable EP</text>
            <text x={sx(xeq)} y={sy(0)-4}  textAnchor="middle" fontSize={8} fill="#555">(±π, 0)</text>
          </g>
        ))}

        {/* Right panel: stability types */}
        <rect x={455} y={oy} width={435} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={672} y={oy+20} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Types of Equilibrium Points</text>

        {[
          {
            name:'Stable Node', color:'#27ae60', shape:'circle',
            cond:'All eigenvalues have Re < 0, both real',
            behav:'Trajectories converge monotonically to EP',
            linz:'A has 2 negative real eigenvalues',
          },
          {
            name:'Stable Spiral (Focus)', color:'#2E75B6', shape:'circle',
            cond:'Complex eigenvalues: λ = σ ± jω, σ < 0',
            behav:'Trajectories spiral inward — damped oscillation',
            linz:'Linearisation captures damped oscillatory behaviour',
          },
          {
            name:'Unstable Node/Spiral', color:'#c0392b', shape:'diamond',
            cond:'Any eigenvalue has Re > 0',
            behav:'Trajectories diverge from EP — unstable',
            linz:'At (±π,0): one positive eigenvalue → saddle/unstable',
          },
          {
            name:'Saddle Point', color:'#e07b00', shape:'diamond',
            cond:'Eigenvalues: one negative, one positive (real)',
            behav:'Stable manifold (in) and unstable manifold (out)',
            linz:'Separatrix divides regions of attraction',
          },
          {
            name:'Centre (Marginally stable)', color:'#9b59b6', shape:'circle',
            cond:'Purely imaginary eigenvalues: λ = ±jω',
            behav:'Closed orbits — undamped oscillation (conservative)',
            linz:'Small perturbation → may be stable or unstable in NL system',
          },
        ].map(({ name, color, shape, cond, behav, linz }, i) => {
          const y = oy + 32 + i*58;
          return (
            <g key={i}>
              <rect x={463} y={y} width={419} height={52} rx={3} fill={i%2===0?'#fafafa':'#fff'} stroke={color} strokeWidth="0.8"/>
              {shape==='circle'
                ? <circle cx={478} cy={y+26} r={8} fill={color} stroke="#fff" strokeWidth="1.5"/>
                : <rect x={470} y={y+18} width={16} height={16} fill={color} stroke="#fff" strokeWidth="1.5" transform={`rotate(45,478,${y+26})`}/>
              }
              <text x={494} y={y+14} fontSize={10.5} fill={color} fontWeight="700">{name}</text>
              <text x={494} y={y+27} fontSize={9}    fill="#555">{cond}</text>
              <text x={494} y={y+39} fontSize={9}    fill="#333">{behav}</text>
              <text x={494} y={y+50} fontSize={8.5}  fill="#888" fontStyle="italic">{linz}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>A damped pendulum has equilibria at (0,0) — stable spiral (all trajectories converge) and at (±π,0) — unstable saddle points. Linearizing at (0,0) gives eigenvalues with negative real parts (stable), while linearizing at (±π,0) gives at least one positive eigenvalue (unstable). The phase portrait shows the full global picture that linearization alone cannot capture.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 5 — Pendulum Worked Example (two operating points)
// ─────────────────────────────────────────────────────────────────
function DiagramPendulumExample() {
  const W = 900, H = 390;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Worked Example — Simple Pendulum Linearized at Two Operating Points</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── TOP: Pendulum schematic + nonlinear EOM ── */}
        <rect x={10} y={8} width={880} height={110} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={26} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Nonlinear Equation of Motion</text>

        {/* Pendulum picture */}
        {/* Pivot */}
        <circle cx={80} cy={50} r={6} fill="#333"/>
        <line x1={50} y1={44} x2={110} y2={44} stroke="#555" strokeWidth="3"/>
        {/* Bob strings for two angles */}
        <line x1={80} y1={50} x2={80} y2={105} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4,2"/>
        <text x={84} y={80} fontSize={9} fill="#2E75B6">θ₀=0</text>
        <line x1={80} y1={50} x2={142} y2={100} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,2"/>
        <text x={140} y={95} fontSize={9} fill="#c0392b">θ₀=π</text>
        {/* Bobs */}
        <circle cx={80}  cy={108} r={10} fill="#2E75B6"/>
        <circle cx={148} cy={104} r={10} fill="#c0392b"/>
        {/* Angle arc */}
        <path d="M 80,62 A 14,14 0 0 1 95,55" fill="none" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={96} y={60} fontSize={9} fill="#e07b00">θ</text>

        {/* EOM */}
        <text x={190} y={44} fontSize={11} fill="#7B0D0D" fontWeight="700">Nonlinear ODE (no friction):</text>
        <text x={190} y={62} fontSize={13} fill="#333" fontFamily="'Courier New',monospace">ml²·θ̈ + mgl·sin(θ) = τ(t)</text>
        <text x={190} y={80} fontSize={11} fill="#555">Divide by ml²:   θ̈ = −(g/l)·sin(θ) + τ/(ml²)</text>
        <text x={190} y={96} fontSize={11} fill="#555" fontFamily="'Courier New',monospace">State vars: x₁ = θ,  x₂ = θ̇  →  ẋ₁=x₂,  ẋ₂=−(g/l)sinx₁ + u</text>

        <text x={550} y={44} fontSize={11} fill="#7B0D0D" fontWeight="700">Find equilibria (ẋ₁=ẋ₂=0, u=0):</text>
        <text x={550} y={62} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">x₂ = 0  and  −(g/l)sin(x₁) = 0</text>
        <text x={550} y={78} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">→  x₁ = nπ  (n = 0, ±1, ±2, …)</text>
        <text x={550} y={96} fontSize={11} fill="#555">EP₁: (0, 0) — hanging down  |  EP₂: (π, 0) — balanced up</text>

        {/* ── LEFT: Linearization at θ₀ = 0 ── */}
        <rect x={10} y={130} width={430} height={250} rx={5} fill="#fff" stroke="#2E75B6" strokeWidth="2"/>
        <rect x={10} y={130} width={430} height={28} rx={5} fill="#2E75B6"/>
        <text x={225} y={149} textAnchor="middle" fontSize={12} fill="#fff" fontWeight="700">Operating Point 1: θ₀ = 0 (hanging down — STABLE)</text>

        {[
          { y:172, c:'#333', t:'Jacobian A = ∂f/∂x at (x₁₀=0, x₂₀=0):' },
          { y:190, c:'#2E75B6', mono:true, t:'A = [ ∂ẋ₁/∂x₁  ∂ẋ₁/∂x₂ ] = [       0          1     ]' },
          { y:206, c:'#2E75B6', mono:true, t:'    [ ∂ẋ₂/∂x₁  ∂ẋ₂/∂x₂ ]   [ −(g/l)cos(0)   0     ]' },
          { y:228, c:'#333', t:'At x₁=0: cos(0) = 1, so:' },
          { y:244, c:'#2E75B6', mono:true, t:'A = [  0     1  ]     B = [ 0 ]' },
          { y:260, c:'#2E75B6', mono:true, t:'    [−g/l   0  ]         [ 1 ]' },
          { y:282, c:'#333', t:'Characteristic equation: det(sI − A) = 0' },
          { y:298, c:'#2E75B6', mono:true, t:'s² + g/l = 0   →   s = ±j√(g/l)' },
          { y:316, c:'#7B0D0D', t:'With g=9.81, l=1m:  ωₙ = √9.81 ≈ 3.13 rad/s' },
        ].map(({ y, c, t, mono }, i) => (
          <text key={i} x={20} y={y} fontSize={10} fill={c}
            fontFamily={mono ? "'Courier New',monospace" : "'Georgia',serif"}>{t}</text>
        ))}

        {/* Stability verdict */}
        <rect x={18} y={328} width={414} height={40} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={225} y={345} textAnchor="middle" fontSize={11} fill="#27ae60" fontWeight="700">✓ Eigenvalues: ±j·3.13 — purely imaginary</text>
        <text x={225} y={360} textAnchor="middle" fontSize={10} fill="#555">Marginally stable (undamped oscillation). Adding damping term</text>
        <text x={225} y={373} textAnchor="middle" fontSize={10} fill="#555">−bθ̇ shifts eigenvalues left → asymptotically stable spiral.</text>

        {/* ── RIGHT: Linearization at θ₀ = π ── */}
        <rect x={455} y={130} width={435} height={250} rx={5} fill="#fff" stroke="#c0392b" strokeWidth="2"/>
        <rect x={455} y={130} width={435} height={28} rx={5} fill="#c0392b"/>
        <text x={672} y={149} textAnchor="middle" fontSize={12} fill="#fff" fontWeight="700">Operating Point 2: θ₀ = π (inverted — UNSTABLE)</text>

        {[
          { y:172, c:'#333', t:'Jacobian A = ∂f/∂x at (x₁₀=π, x₂₀=0):' },
          { y:190, c:'#c0392b', mono:true, t:'A = [       0           1      ]' },
          { y:206, c:'#c0392b', mono:true, t:'    [ −(g/l)cos(π)      0      ]' },
          { y:224, c:'#333', t:'At x₁=π: cos(π) = −1, so −(g/l)cos(π) = +g/l' },
          { y:242, c:'#c0392b', mono:true, t:'A = [  0     1  ]     B = [ 0 ]' },
          { y:258, c:'#c0392b', mono:true, t:'    [+g/l   0  ]         [ 1 ]' },
          { y:278, c:'#333', t:'Characteristic equation:' },
          { y:294, c:'#c0392b', mono:true, t:'s² − g/l = 0   →   s = ±√(g/l)' },
          { y:312, c:'#7B0D0D', t:'With g=9.81, l=1m:  s = ±3.13  (REAL, opposite signs)' },
        ].map(({ y, c, t, mono }, i) => (
          <text key={i} x={465} y={y} fontSize={10} fill={c}
            fontFamily={mono ? "'Courier New',monospace" : "'Georgia',serif"}>{t}</text>
        ))}

        {/* Stability verdict */}
        <rect x={463} y={328} width={419} height={40} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={672} y={345} textAnchor="middle" fontSize={11} fill="#c0392b" fontWeight="700">✗ Eigenvalues: +3.13 and −3.13 — one positive real part</text>
        <text x={672} y={360} textAnchor="middle" fontSize={10} fill="#555">Saddle point — UNSTABLE. State diverges exponentially from EP.</text>
        <text x={672} y={373} textAnchor="middle" fontSize={10} fill="#555">Control design needed to stabilize (e.g. pole placement at s&lt;0).</text>
      </svg>
      <div style={S.dCaption}>The same pendulum nonlinear ODE produces two completely different linear models depending on which equilibrium is chosen. At θ₀=0 (hanging), linearization gives purely imaginary poles — undamped oscillation. At θ₀=π (inverted), it gives real poles of opposite sign — a saddle/unstable equilibrium. This is why stabilizing an inverted pendulum (like a rocket or Segway) requires active feedback control.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 6 — Linearization Error & Valid Region
// ─────────────────────────────────────────────────────────────────
function DiagramLinearizationError() {
  const W = 900, H = 340;

  // f(x) = sin(x), linearised at x0=0 → fL(x) = x
  // Error = |f(x) - fL(x)| = |sin(x) - x|
  const xMin = 0, xMax = 2.5;
  const ox = 30, oy = 30, PW = 380, PH = 240;
  const sx = x => ox + 40 + (x-xMin)/(xMax-xMin)*(PW-60);
  const sy = y => oy + PH - 20 - (y)/(0.8)*(PH-40);

  const N = 100;
  const errPts = [], truePts = [], linPts = [];
  for (let i=0; i<=N; i++) {
    const x = xMin + i*(xMax-xMin)/N;
    errPts.push([sx(x), sy(Math.abs(Math.sin(x) - x))]);
    truePts.push([sx(x), sy(Math.sin(x))]);
    linPts.push([sx(x), sy(x)]);
  }
  const mkD = pts => pts.map(([x,y],i) => `${i===0?'M':'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

  // Percentage error at specific x values
  const errData = [0.1, 0.3, 0.5, 0.8, 1.0, 1.5, 2.0].map(x => ({
    x,
    pct: Math.abs((Math.sin(x) - x) / (Math.abs(Math.sin(x)) > 1e-6 ? Math.sin(x) : 1)) * 100,
  }));

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Linearization Error — Quantifying Approximation Accuracy vs Perturbation Size</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <clipPath id="le-clip">
            <rect x={ox+38} y={oy} width={PW-56} height={PH-18}/>
          </clipPath>
        </defs>

        {/* LEFT: Error plot */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">|sin(x) − x|  Error vs Perturbation x (x₀=0)</text>

        {/* Grid */}
        {[0.5, 1.0, 1.5, 2.0, 2.5].map(v => (
          <g key={v}>
            <line x1={sx(v)} y1={oy+10} x2={sx(v)} y2={oy+PH-18} stroke="#f0cece" strokeWidth="1"/>
            <text x={sx(v)} y={oy+PH-5} textAnchor="middle" fontSize={9} fill="#aaa">{v}</text>
          </g>
        ))}
        {[0.1, 0.2, 0.4, 0.6, 0.8].map(v => (
          <g key={v}>
            <line x1={ox+38} y1={sy(v)} x2={ox+PW-18} y2={sy(v)} stroke="#f0cece" strokeWidth="1"/>
            <text x={ox+34} y={sy(v)+3} textAnchor="end" fontSize={9} fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={ox+38} y1={oy+PH-18} x2={ox+PW-14} y2={oy+PH-18} stroke="#aaa" strokeWidth="1.5"/>
        <line x1={ox+38} y1={oy+10} x2={ox+38} y2={oy+PH-18} stroke="#aaa" strokeWidth="1.5"/>
        <text x={ox+PW-10} y={oy+PH-14} fontSize={9} fill="#aaa">x</text>
        <text x={ox+40} y={oy+6} fontSize={9} fill="#aaa">err</text>

        {/* True curve */}
        <path d={mkD(truePts)} fill="none" stroke="#c0392b" strokeWidth="2" opacity="0.4" clipPath="url(#le-clip)"/>
        {/* Linear approx */}
        <path d={mkD(linPts)} fill="none" stroke="#e07b00" strokeWidth="1.5" strokeDasharray="6,4" clipPath="url(#le-clip)"/>
        {/* Error */}
        <path d={mkD(errPts)} fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#le-clip)"/>

        {/* Acceptable error threshold line */}
        <line x1={ox+38} y1={sy(0.05)} x2={ox+PW-18} y2={sy(0.05)} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="5,3" opacity="0.8"/>
        <text x={ox+PW-14} y={sy(0.05)-4} fontSize={8.5} fill="#27ae60">5% err</text>

        {/* Valid region annotation */}
        <rect x={ox+38} y={oy+10} width={sx(0.55)-ox-38} height={PH-28} fill="#27ae60" opacity="0.07"/>
        <text x={(ox+38 + sx(0.55))/2} y={oy+PH-25} textAnchor="middle" fontSize={8} fill="#27ae60">≤5%</text>

        <text x={ox+38} y={oy+PH+18} fontSize={9} fill="#555">Perturbation Δx (rad)</text>

        {/* Labels */}
        <text x={sx(1.8)} y={sy(0.22)} fontSize={9}   fill="#2E75B6" fontWeight="700">|error|</text>
        <text x={sx(1.8)} y={sy(0.50)} fontSize={9}   fill="#e07b00">x (linear)</text>
        <text x={sx(1.6)} y={sy(0.72)} fontSize={9}   fill="#c0392b" opacity="0.6">sin(x)</text>

        {/* MIDDLE: Error table */}
        <rect x={430} y={oy} width={200} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={530} y={oy+18} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Error at key Δx</text>
        {/* Header */}
        <rect x={430} y={oy+24} width={200} height={22} rx={2} fill="#c0392b"/>
        <text x={480} y={oy+39} textAnchor="middle" fontSize={10} fill="#fff" fontWeight="700">|Δx| (rad)</text>
        <text x={580} y={oy+39} textAnchor="middle" fontSize={10} fill="#fff" fontWeight="700">% Error</text>
        {errData.map(({ x, pct }, i) => {
          const bg = pct < 5 ? '#f0fff4' : pct < 20 ? '#fff5e6' : '#FEF0F0';
          const fc = pct < 5 ? '#27ae60' : pct < 20 ? '#e07b00' : '#c0392b';
          return (
            <g key={i}>
              <rect x={430} y={oy+46+i*26} width={200} height={26} fill={bg}/>
              <line x1={430} y1={oy+46+i*26} x2={630} y2={oy+46+i*26} stroke="#f0cece" strokeWidth="0.8"/>
              <text x={480} y={oy+63+i*26} textAnchor="middle" fontSize={11} fill="#333" fontFamily="'Courier New',monospace">{x.toFixed(1)}</text>
              <text x={580} y={oy+63+i*26} textAnchor="middle" fontSize={11} fill={fc} fontWeight="700">{pct.toFixed(1)}%</text>
            </g>
          );
        })}
        <line x1={530} y1={oy+24} x2={530} y2={oy+PH-4} stroke="#f0cece" strokeWidth="1"/>

        {/* RIGHT: Rules of thumb */}
        <rect x={645} y={oy} width={248} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={769} y={oy+18} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Rules of Thumb</text>
        {[
          { pct:'&lt; 5%', color:'#27ae60', bg:'#f0fff4', bc:'#27ae60', cond:'|Δx| &lt; ~0.5 rad', rule:'Linearization excellent\nSafe to use linear controller' },
          { pct:'5–20%', color:'#e07b00', bg:'#fff5e6', bc:'#e07b00', cond:'|Δx| ~ 0.5–1.0 rad', rule:'Caution: some error\nMonitor performance' },
          { pct:'&gt; 20%', color:'#c0392b', bg:'#FEF0F0', bc:'#c0392b', cond:'|Δx| &gt; 1.0 rad', rule:'Linearization poor\nUse gain scheduling\nor nonlinear control' },
        ].map(({ pct, color, bg, bc, cond, rule }, i) => (
          <g key={i}>
            <rect x={653} y={oy+28+i*72} width={232} height={66} rx={3} fill={bg} stroke={bc} strokeWidth="1.2"/>
            <text x={769} y={oy+46+i*72} textAnchor="middle" fontSize={12} fill={color} fontWeight="700">{pct} error</text>
            <text x={769} y={oy+60+i*72} textAnchor="middle" fontSize={10} fill="#555">{cond}</text>
            {rule.split('\n').map((l,j) => (
              <text key={j} x={769} y={oy+73+i*72+j*13} textAnchor="middle" fontSize={9.5} fill={color}>{l}</text>
            ))}
          </g>
        ))}

        {/* Remainder formula */}
        <rect x={653} y={oy+244} width={232} height={48} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.2"/>
        <text x={769} y={oy+260} textAnchor="middle" fontSize={10} fill="#9b59b6" fontWeight="700">Remainder (Lagrange):</text>
        <text x={769} y={oy+275} textAnchor="middle" fontSize={10} fill="#333" fontFamily="'Courier New',monospace">R = f″(c)·(Δx)²/2!</text>
        <text x={769} y={oy+288} textAnchor="middle" fontSize={9} fill="#555">c between x₀ and x; grows as (Δx)²</text>
      </svg>
      <div style={S.dCaption}>For sin(x) linearized at x₀=0, the error grows approximately as (Δx)³/6 (since the 2nd derivative at 0 is zero). Below |Δx|=0.3 rad the error is under 1.5% — excellent. Above |Δx|=1.0 rad errors exceed 15% — the linear model is unreliable. This quantifies when a single operating-point linearization is sufficient and when gain scheduling or nonlinear control is needed.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 7 — Gain Scheduling
// ─────────────────────────────────────────────────────────────────
function DiagramGainScheduling() {
  const W = 900, H = 360;

  // Nonlinear curve with 4 operating points and local linear models
  const fn  = x => 1.5*Math.tanh(x*0.8) + 0.3*Math.sin(2.5*x);
  const dfn = x => 1.5*0.8*(1-Math.tanh(x*0.8)**2) + 0.3*2.5*Math.cos(2.5*x);
  const xMin=-3.2, xMax=3.2, yMin=-2.2, yMax=2.2;
  const ox=20, oy=34, PW=430, PH=270;
  const sx=x=>ox+32+(x-xMin)/(xMax-xMin)*(PW-56);
  const sy=y=>oy+PH-24-(y-yMin)/(yMax-yMin)*(PH-48);

  const N=120;
  const mainD=[];
  for(let i=0;i<=N;i++){
    const x=xMin+i*(xMax-xMin)/N;
    mainD.push(`${i===0?'M':'L'}${sx(x).toFixed(1)},${sy(fn(x)).toFixed(1)}`);
  }

  const ops = [
    { x:-2.4, color:'#c0392b',  label:'OP1' },
    { x:-0.8, color:'#e07b00',  label:'OP2' },
    { x: 0.8, color:'#27ae60',  label:'OP3' },
    { x: 2.2, color:'#2E75B6',  label:'OP4' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Gain Scheduling — Multiple Linearized Models for Varying Operating Conditions</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <clipPath id="gs-clip">
            <rect x={ox+30} y={oy} width={PW-54} height={PH-22}/>
          </clipPath>
          <marker id="gs-arr" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#555"/>
          </marker>
        </defs>

        {/* Main plot */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-10} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Nonlinear System with Four Gain-Scheduled Operating Points</text>

        {/* Axes */}
        <line x1={ox+30} y1={sy(0)} x2={ox+PW-22} y2={sy(0)} stroke="#ccc" strokeWidth="1.2"/>
        <line x1={sx(0)} y1={oy+8} x2={sx(0)} y2={oy+PH-22} stroke="#ccc" strokeWidth="1.2"/>
        {[-3,-2,-1,1,2,3].map(v=>(
          <g key={v}>
            <line x1={sx(v)} y1={oy+8} x2={sx(v)} y2={oy+PH-22} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={sx(v)} y={oy+PH-8} textAnchor="middle" fontSize={8} fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Nonlinear curve */}
        <path d={mainD.join(' ')} fill="none" stroke="#1a1a2e" strokeWidth="2.5"/>

        {/* Local linear models */}
        {ops.map(({ x, color, label }) => {
          const y0 = fn(x), slope = dfn(x);
          const hw = 0.75;
          const x1 = x-hw, x2 = x+hw;
          const d = `M ${sx(x1).toFixed(1)},${sy(y0+slope*(x1-x)).toFixed(1)} L ${sx(x2).toFixed(1)},${sy(y0+slope*(x2-x)).toFixed(1)}`;
          return (
            <g key={label}>
              <path d={d} fill="none" stroke={color} strokeWidth="2" strokeDasharray="7,4" clipPath="url(#gs-clip)"/>
              <circle cx={sx(x)} cy={sy(y0)} r={7} fill={color} stroke="#fff" strokeWidth="2"/>
              <text x={sx(x)} y={sy(y0)-12} textAnchor="middle" fontSize={9.5} fill={color} fontWeight="700">{label}</text>
              <text x={sx(x)} y={sy(y0)-2} textAnchor="middle" fontSize={8} fill={color}>K={slope.toFixed(2)}</text>
            </g>
          );
        })}

        {/* Scheduling variable axis label */}
        <text x={ox+PW-18} y={sy(0)+4} fontSize={9} fill="#aaa">u</text>
        <text x={sx(0)+4}  y={oy+10}   fontSize={9} fill="#aaa">y</text>

        {/* Legend */}
        <line x1={ox+36} y1={oy+PH-8} x2={ox+56} y2={oy+PH-8} stroke="#1a1a2e" strokeWidth="2.5"/>
        <text x={ox+60} y={oy+PH-4} fontSize={9} fill="#333">Nonlinear f(u)</text>
        <line x1={ox+140} y1={oy+PH-8} x2={ox+160} y2={oy+PH-8} stroke="#555" strokeWidth="1.5" strokeDasharray="5,3"/>
        <text x={ox+164} y={oy+PH-4} fontSize={9} fill="#555">Local linear model</text>

        {/* ── RIGHT: Gain scheduling system description ── */}
        <rect x={465} y={oy} width={425} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={677} y={oy+18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Gain Scheduling Architecture</text>

        {/* Block diagram of gain scheduler */}
        {/* Reference */}
        <text x={473} y={oy+50} fontSize={10} fill="#c0392b" fontWeight="700">r(t)</text>
        <line x1={490} y1={oy+47} x2={510} y2={oy+47} stroke="#c0392b" strokeWidth="1.5" markerEnd="url(#gs-arr)"/>

        {/* Controller block */}
        <rect x={510} y={oy+32} width={80} height={30} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={550} y={oy+48} textAnchor="middle" fontSize={10} fill="#c0392b" fontWeight="700">C(s, Kᵢ)</text>
        <line x1={590} y1={oy+47} x2={620} y2={oy+47} stroke="#333" strokeWidth="1.5" markerEnd="url(#gs-arr)"/>

        {/* Plant */}
        <rect x={620} y={oy+32} width={80} height={30} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={660} y={oy+48} textAnchor="middle" fontSize={10} fill="#2E75B6" fontWeight="700">G(s, θ)</text>
        <line x1={700} y1={oy+47} x2={760} y2={oy+47} stroke="#333" strokeWidth="1.5" markerEnd="url(#gs-arr)"/>
        <text x={770} y={oy+51} fontSize={10} fill="#27ae60" fontWeight="700">y(t)</text>

        {/* Feedback */}
        <circle cx={750} cy={oy+47} r={3} fill="#333"/>
        <line x1={750} y1={oy+50} x2={750} y2={oy+95} stroke="#333" strokeWidth="1.5"/>
        <line x1={750} y1={oy+95} x2={490} y2={oy+95} stroke="#333" strokeWidth="1.5" markerEnd="url(#gs-arr)"/>

        {/* Scheduling variable block */}
        <rect x={620} y={oy+100} width={80} height={30} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={660} y={oy+116} textAnchor="middle" fontSize={9} fill="#27ae60" fontWeight="700">Measure θ</text>
        <text x={660} y={oy+127} textAnchor="middle" fontSize={8} fill="#555">(sched. var.)</text>
        <line x1={750} y1={oy+50} x2={720} y2={oy+50} stroke="#27ae60" strokeWidth="1" strokeDasharray="3,2"/>
        <line x1={720} y1={oy+50} x2={720} y2={oy+100} stroke="#27ae60" strokeWidth="1" strokeDasharray="3,2"/>

        {/* Lookup table */}
        <rect x={510} y={oy+100} width={100} height={30} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.5"/>
        <text x={560} y={oy+116} textAnchor="middle" fontSize={9} fill="#9b59b6" fontWeight="700">Gain Table</text>
        <text x={560} y={oy+127} textAnchor="middle" fontSize={8} fill="#555">θ → Kᵢ lookup</text>
        <line x1={620} y1={oy+115} x2={612} y2={oy+115} stroke="#9b59b6" strokeWidth="1.5" markerEnd="url(#gs-arr)"/>
        {/* Update controller */}
        <line x1={560} y1={oy+100} x2={560} y2={oy+62} stroke="#9b59b6" strokeWidth="1.5" markerEnd="url(#gs-arr)" strokeDasharray="4,2"/>

        {/* Operating points table */}
        <text x={473} y={oy+152} fontSize={10} fill="#7B0D0D" fontWeight="700">Linearized models at each operating point:</text>
        <rect x={473} y={oy+158} width={408} height={20} rx={2} fill="#c0392b"/>
        <text x={530} y={oy+173} textAnchor="middle" fontSize={9} fill="#fff" fontWeight="700">OP</text>
        <text x={600} y={oy+173} textAnchor="middle" fontSize={9} fill="#fff" fontWeight="700">θ₀ (sched. var.)</text>
        <text x={710} y={oy+173} textAnchor="middle" fontSize={9} fill="#fff" fontWeight="700">Local Gain K</text>
        <text x={840} y={oy+173} textAnchor="middle" fontSize={9} fill="#fff" fontWeight="700">Controller Kᵢ</text>

        {ops.map(({ x, color, label }, i) => {
          const slope = dfn(x);
          return (
            <g key={label}>
              <rect x={473} y={oy+178+i*22} width={408} height={22} fill={i%2===0?'#fff5f5':'#fff'}/>
              <text x={530} y={oy+194+i*22} textAnchor="middle" fontSize={10} fill={color} fontWeight="700">{label}</text>
              <text x={600} y={oy+194+i*22} textAnchor="middle" fontSize={10} fill="#333" fontFamily="'Courier New',monospace">{x.toFixed(1)}</text>
              <text x={710} y={oy+194+i*22} textAnchor="middle" fontSize={10} fill="#333" fontFamily="'Courier New',monospace">{slope.toFixed(3)}</text>
              <text x={840} y={oy+194+i*22} textAnchor="middle" fontSize={10} fill={color} fontFamily="'Courier New',monospace">{(1/slope*2).toFixed(2)}</text>
            </g>
          );
        })}
        <line x1={530} y1={oy+178} x2={530} y2={oy+178+ops.length*22} stroke="#f0cece" strokeWidth="1"/>
        <line x1={565} y1={oy+178} x2={565} y2={oy+178+ops.length*22} stroke="#f0cece" strokeWidth="1"/>
        <line x1={660} y1={oy+178} x2={660} y2={oy+178+ops.length*22} stroke="#f0cece" strokeWidth="1"/>
        <line x1={770} y1={oy+178} x2={770} y2={oy+178+ops.length*22} stroke="#f0cece" strokeWidth="1"/>

        <rect x={473} y={oy+182+ops.length*22} width={408} height={22} rx={2} fill="#fff5e6" stroke="#e07b00" strokeWidth="1"/>
        <text x={677} y={oy+197+ops.length*22} textAnchor="middle" fontSize={9.5} fill="#e07b00">Between operating points: interpolate gains smoothly to avoid switching transients</text>
      </svg>
      <div style={S.dCaption}>Gain scheduling pre-computes linearized models and controllers at multiple operating points, then automatically selects or interpolates the appropriate controller gains based on a measured scheduling variable (e.g. speed, altitude, temperature). Used extensively in aircraft autopilots (different flight envelopes), engine control, and industrial process control where a single linearization is insufficient.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 8 — Nonlinear vs Linearized Step Response Comparison
// ─────────────────────────────────────────────────────────────────
function DiagramResponseComparison() {
  const W = 900, H = 360;

  // Simulate nonlinear pendulum (ẋ₁=x₂, ẋ₂=−(g/l)sin(x₁)) vs linear (ẋ₂=−(g/l)x₁)
  // with step input added as small/medium/large
  const g=9.81, l=1.0, omega2=g/l;
  const dt=0.02, steps=250;

  const simulate = (nlFlag, x1_0, u=0) => {
    let x1=x1_0, x2=0;
    const pts=[[0,x1]];
    for(let i=0;i<steps;i++){
      const f2 = nlFlag ? -(omega2)*Math.sin(x1) : -(omega2)*x1;
      x2 += dt*(f2+u);
      x1 += dt*x2;
      pts.push([(i+1)*dt, x1]);
    }
    return pts;
  };

  // Three scenarios: small (0.2 rad), medium (0.8 rad), large (1.5 rad)
  const scenarios = [
    { ic:0.2, label:'Small (0.2 rad)',   color:'#27ae60' },
    { ic:0.8, label:'Medium (0.8 rad)',  color:'#e07b00' },
    { ic:1.5, label:'Large (1.5 rad)',   color:'#c0392b' },
  ];

  const PW=390, PH=270;
  const tMax=steps*dt, ySpan=1.8;
  const ox=18, oy=36;
  const sx=t=>ox+36+(t/tMax)*(PW-50);
  const sy=y=>oy+PH-24-((y+ySpan/2)/ySpan)*(PH-48);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Linear vs Nonlinear Simulation — When Does the Linearized Model Fail?</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <clipPath id="rc-clip">
            <rect x={ox+34} y={oy} width={PW-48} height={PH-22}/>
          </clipPath>
        </defs>

        {/* PLOT */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">θ(t): Nonlinear (solid) vs Linearised (dashed)</text>

        {/* Grid */}
        {[1,2,3,4].map(tv => (
          <g key={tv}>
            <line x1={sx(tv)} y1={oy+8} x2={sx(tv)} y2={oy+PH-22} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={sx(tv)} y={oy+PH-8} textAnchor="middle" fontSize={8} fill="#aaa">{tv}s</text>
          </g>
        ))}
        {[-0.6,0,0.6].map(yv=>(
          <g key={yv}>
            <line x1={ox+34} y1={sy(yv)} x2={ox+PW-14} y2={sy(yv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={ox+30} y={sy(yv)+3} textAnchor="end" fontSize={8} fill="#aaa">{yv}</text>
          </g>
        ))}

        {/* Zero line */}
        <line x1={ox+34} y1={sy(0)} x2={ox+PW-14} y2={sy(0)} stroke="#ddd" strokeWidth="1.2"/>

        {/* Axes */}
        <line x1={ox+34} y1={oy+PH-22} x2={ox+PW-10} y2={oy+PH-22} stroke="#aaa" strokeWidth="1.5"/>
        <line x1={ox+34} y1={oy+8}     x2={ox+34}     y2={oy+PH-22} stroke="#aaa" strokeWidth="1.5"/>
        <text x={ox+PW-6} y={oy+PH-18} fontSize={8} fill="#aaa">t</text>
        <text x={ox+38}   y={oy+6}      fontSize={8} fill="#aaa">θ</text>

        {/* Curves */}
        {scenarios.map(({ ic, color }) => {
          const nlPts = simulate(true, ic);
          const linPts = simulate(false, ic);

          const nlD  = nlPts.map(([t,y],i)=>`${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');
          const linD = linPts.map(([t,y],i)=>`${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');

          return (
            <g key={ic}>
              <path d={nlD}  fill="none" stroke={color} strokeWidth="2.2" clipPath="url(#rc-clip)"/>
              <path d={linD} fill="none" stroke={color} strokeWidth="1.5" strokeDasharray="6,4" clipPath="url(#rc-clip)" opacity="0.8"/>
            </g>
          );
        })}

        {/* IC labels */}
        {scenarios.map(({ ic, color, label }) => (
          <g key={ic}>
            <circle cx={sx(0)} cy={sy(ic)} r={4} fill={color}/>
          </g>
        ))}

        {/* RIGHT: Analysis panel */}
        <rect x={425} y={oy} width={465} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={657} y={oy+18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Discrepancy Analysis</text>

        {scenarios.map(({ ic, color, label }, i) => {
          // Compute max deviation between nonlinear and linear
          const nl = simulate(true, ic);
          const lin = simulate(false, ic);
          const maxDev = Math.max(...nl.map(([t,y],j) => Math.abs(y - (lin[j]?.[1]??0))));
          const pctErr = (maxDev/ic*100).toFixed(0);

          const ok = ic < 0.35;
          const warn = ic < 0.9;

          return (
            <g key={i}>
              <rect x={433} y={oy+26+i*80} width={449} height={72} rx={3}
                fill={ok?'#f0fff4':warn?'#fff5e6':'#FEF0F0'}
                stroke={color} strokeWidth="1.2"/>
              <circle cx={448} cy={oy+50+i*80} r={6} fill={color}/>
              <text x={460} y={oy+38+i*80} fontSize={11} fill={color} fontWeight="700">{label} initial condition</text>

              <line x1={433} y1={oy+42+i*80} x2={882} y2={oy+42+i*80} stroke={color} strokeWidth="0.6" opacity="0.5"/>

              <text x={440} y={oy+56+i*80} fontSize={10} fill="#333">
                Max deviation: {maxDev.toFixed(4)} rad ≈ {pctErr}% of IC
              </text>
              <text x={440} y={oy+70+i*80} fontSize={10} fill="#333">
                Period: NL = {(2*Math.PI/Math.sqrt(g/l*(Math.abs(ic)<0.01?1:1))).toFixed(2)}s  |
                Linear = {(2*Math.PI/Math.sqrt(g/l)).toFixed(2)}s
              </text>
              <text x={440} y={oy+84+i*80} fontSize={10} fill={ok?'#27ae60':warn?'#e07b00':'#c0392b'} fontWeight="700">
                {ok ? '✓ Linear model accurate — controller design valid'
                  : warn ? '⚠ Noticeable divergence — moderate error accumulates'
                  : '✗ Significant divergence — nonlinear control recommended'}
              </text>
            </g>
          );
        })}

        {/* Legend */}
        <rect x={433} y={oy+268} width={449} height={18} rx={2} fill="#fffcf5" stroke="#f0cece"/>
        <line x1={440} y1={oy+278} x2={464} y2={oy+278} stroke="#555" strokeWidth="2.2"/>
        <text x={467} y={oy+282} fontSize={9} fill="#333">Nonlinear simulation</text>
        <line x1={580} y1={oy+278} x2={604} y2={oy+278} stroke="#555" strokeWidth="1.5" strokeDasharray="5,3"/>
        <text x={607} y={oy+282} fontSize={9} fill="#333">Linearised model</text>
      </svg>
      <div style={S.dCaption}>Simulating the pendulum from three initial conditions (small, medium, large angle) shows the divergence between the nonlinear truth and the linearised approximation. For the small perturbation (0.2 rad), curves are nearly identical throughout. For the large perturbation (1.5 rad), the linear model predicts incorrect frequency and amplitude — the system is operating well outside the valid linearization region.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────
export default function Linearization() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Control Systems — Linearization</h1>
      <p style={S.p}>
        Almost every real physical system is nonlinear — springs saturate, actuators hit limits, fluid flow obeys square-root laws, and pendulums involve trigonometric functions. Yet the most powerful tools of control theory (transfer functions, Bode plots, root locus, pole placement, LQR) require a linear model. Linearization bridges this gap: by approximating the nonlinear system as a linear one in a small neighborhood around a chosen operating point, we recover the full power of linear analysis — provided the system stays near that point. Understanding when this approximation is valid, and what to do when it is not, is one of the most important skills in control engineering.
      </p>

      <InfoBox title="Core Concepts at a Glance" items={[
        "Linearization replaces f(x) ≈ f(x₀) + f′(x₀)·Δx near operating point x₀ — a first-order Taylor expansion",
        "For a system ẋ = f(x,u): linearised model is Δẋ = AΔx + BΔu where A,B are Jacobian matrices at (x₀,u₀)",
        "An equilibrium point (x₀,u₀) satisfies f(x₀,u₀) = 0 — all state derivatives are zero",
        "Stability of linearised model: all eigenvalues of A must have negative real parts",
        "The linearised model is only valid for small perturbations Δx, Δu around the operating point",
        "Different operating points yield different A,B,C,D matrices — and different stability properties",
        "Gain scheduling extends linearization globally: pre-compute multiple models and switch/interpolate",
        "Lyapunov's indirect method: if the linearisation is stable (or unstable), so is the nonlinear system locally",
      ]}/>

      <h2 style={S.h2}>Geometric Intuition — The Tangent-Line Approximation</h2>
      <p style={S.p}>
        The simplest view of linearization is geometric: replace the nonlinear curve with its tangent line at the operating point. The tangent line has the same value and the same slope as the nonlinear function at x₀, and it agrees closely with the function in a small interval around x₀. This works for any smooth nonlinear function — cubic, trigonometric, exponential, or otherwise — because all smooth functions look locally linear when you zoom in far enough.
      </p>
      <DiagramGeometricIntuition/>
      <Table2 rows={[
        ["Operating point x₀","The chosen nominal value of the input (and state) around which we linearize\nTypically an equilibrium, steady-state, or desired set-point"],
        ["Perturbation Δx","Δx = x − x₀: small deviation from the operating point\nLinear model applies to Δx, not to x directly"],
        ["Linear approximation","f(x) ≈ f(x₀) + f′(x₀)·Δx  →  Δy ≈ K·Δx  where K = f′(x₀)\nK is the local gain — different at every operating point"],
        ["Local gain K","K = df/dx evaluated at x₀\nFor nonlinear f: K varies with x₀ — this is the core reason for gain scheduling"],
        ["Valid region","Region around x₀ where linear model error is acceptably small\nDepends on curvature of f and required accuracy"],
        ["Why tangent?","Tangent is the best possible linear approximation at a point\nMinimises first-order error; higher-order terms (Δx², Δx³) are remainder"],
      ]}/>

      <h2 style={S.h2}>Taylor Series — The Mathematical Foundation</h2>
      <p style={S.p}>
        Linearization is the formal truncation of a Taylor series after the first-order term. The Taylor series expands any infinitely differentiable function f as a power series around x₀. Each additional term improves accuracy but complicates the model. Control engineers keep only the zeroth- and first-order terms — the constant offset and the slope — discarding all higher-order (quadratic and above) terms. This is justified by the smallness of Δx: if |Δx| is small, then (Δx)² is negligible, (Δx)³ even more so.
      </p>
      <DiagramTaylorSeries/>
      <Table2 rows={[
        ["Taylor expansion","f(x) = f(x₀) + f′(x₀)(x−x₀) + f″(x₀)(x−x₀)²/2! + f‴(x₀)(x−x₀)³/3! + …\nInfinite series; exact for analytic functions"],
        ["Zeroth-order term","f(x₀) — the function value at the operating point\nSubtracted out when working in perturbation variables: Δy = y − y₀"],
        ["First-order term","f′(x₀)·(x−x₀) = K·Δx — the linear term\nThis IS the linearisation: Δy ≈ K·Δx"],
        ["Second-order remainder","f″(x₀)·(Δx)²/2 — the leading error term of linearization\nGrows as (Δx)²; small if |Δx| ≪ 1"],
        ["Lagrange remainder","R_n = f^(n+1)(c)·(Δx)^(n+1)/(n+1)!  for some c between x₀ and x\nBound on truncation error"],
        ["When higher order matters","When Δx is not small, or f has large curvature (|f″| large near x₀)\nExample: near an inflection point, f″=0 → quadratic error term is small → linearization is good"],
        ["Multi-variable Taylor","f(x₀+Δx) ≈ f(x₀) + ∇f(x₀)·Δx + ½ΔxᵀH(x₀)Δx + …\nH = Hessian matrix; ∇f = gradient (the Jacobian row for scalar f)"],
        ["Absolute vs relative error","Absolute: |f(x) − fL(x)|  |  Relative: |f(x)−fL(x)|/|f(x)|\nUse relative error to compare across different operating points"],
      ]}/>

      <h2 style={S.h2}>Multivariable Linearization — Jacobian Matrices</h2>
      <p style={S.p}>
        For a system described by n coupled nonlinear ODEs with m inputs and p outputs, linearization extends straightforwardly via partial derivatives. The Jacobian matrix generalises the single-variable derivative f′(x₀) to the multivariable setting: the (i,j) entry of the Jacobian is ∂fᵢ/∂xⱼ — the rate of change of the ith equation with respect to the jth state variable. This gives the four system matrices A, B, C, D of the linearised state-space model.
      </p>
      <DiagramJacobian/>
      <Table2 rows={[
        ["Jacobian matrix","Matrix of all partial derivatives of a vector-valued function\nA = ∂f/∂x: each row i, column j gives ∂fᵢ/∂xⱼ"],
        ["System matrix A [n×n]","Aᵢⱼ = ∂fᵢ/∂xⱼ|(x₀,u₀)\nDiagonal entries: self-feedback (how xᵢ feeds back to ẋᵢ)\nOff-diagonal: coupling between states"],
        ["Input matrix B [n×m]","Bᵢⱼ = ∂fᵢ/∂uⱼ|(x₀,u₀)\nHow the j-th input drives the i-th state equation"],
        ["Output matrix C [p×n]","Cᵢⱼ = ∂gᵢ/∂xⱼ|(x₀,u₀) where y=g(x,u)\nHow states appear in the measured outputs"],
        ["Feedthrough D [p×m]","Dᵢⱼ = ∂gᵢ/∂uⱼ|(x₀,u₀)\nDirect input→output path; often D=0 in physical systems"],
        ["Superposition principle","After linearisation, Δ variables obey superposition\nResponses to multiple small inputs can be added"],
        ["Transfer function","G(s) = C(sI−A)⁻¹B + D  (computed from linearised matrices)\nValid for frequency-domain analysis near operating point"],
        ["Eigenvalues of A","Eigenvalues λᵢ of A are the poles of the linearised transfer function\nStability: all Re(λᵢ) < 0 ⟺ locally asymptotically stable"],
      ]}/>

      <h2 style={S.h2}>Equilibrium Points &amp; Local Stability</h2>
      <p style={S.p}>
        An equilibrium point (also called a fixed point or singular point) is a constant solution of the nonlinear ODE — a state where all state derivatives are zero. Linearization is always performed at an equilibrium. The type of equilibrium (stable node, stable spiral, saddle, unstable node) is determined by the eigenvalues of the Jacobian matrix A at that point, and each type corresponds to a distinctive pattern in the phase portrait.
      </p>
      <DiagramEquilibrium/>
      <Table2 rows={[
        ["Equilibrium point","f(x₀, u₀) = 0 — all state derivatives vanish\nSystem stays at x₀ forever if started there and undisturbed"],
        ["Stable equilibrium","All nearby trajectories converge to x₀\nLinearisation: all eigenvalues of A have Re < 0"],
        ["Unstable equilibrium","Nearby trajectories diverge from x₀\nLinearisation: at least one eigenvalue has Re > 0"],
        ["Stable node","All real negative eigenvalues: λ₁ < λ₂ < 0\nTrajectories approach EP along slowest eigenvector direction"],
        ["Stable spiral (focus)","Complex eigenvalues: σ ± jω, σ < 0\nTrajectories spiral inward — damped oscillation around EP"],
        ["Saddle point","One positive, one negative real eigenvalue\nStable manifold (trajectories in) and unstable manifold (out)"],
        ["Centre","Purely imaginary eigenvalues: λ = ±jω\nClosed periodic orbits in linear case — may differ in nonlinear system"],
        ["Lyapunov indirect method","If linearisation has ALL eigenvalues with Re≠0: nonlinear system has same stability type locally\nIf any eigenvalue has Re=0: linearisation is inconclusive — need Lyapunov analysis"],
      ]}/>

      <h2 style={S.h2}>Worked Example — Pendulum at Two Operating Points</h2>
      <p style={S.p}>
        The simple pendulum is the canonical example for linearization because it has two physically meaningful equilibria — hanging down (stable) and balanced upward (unstable) — and because the nonlinearity is a familiar trigonometric function. The same differential equation, linearized at two different equilibria, produces two completely different linear models with opposite stability characteristics.
      </p>
      <DiagramPendulumExample/>
      <Table2 rows={[
        ["Pendulum ODE","ml²·θ̈ + mgl·sin(θ) = τ\nNonlinearity: sin(θ) ≠ θ except for small θ"],
        ["State variables","x₁ = θ (angle), x₂ = θ̇ (angular velocity)\nẋ₁ = x₂,  ẋ₂ = −(g/l)sin(x₁) + u/(ml²)"],
        ["Equilibria (u=0)","x₁ = nπ, x₂ = 0 for integer n\nPhysically: hanging (even n) or balanced (odd n)"],
        ["Linearization at θ₀=0","A = [0, 1; −g/l, 0], eigenvalues ±j√(g/l)\nPurely imaginary → undamped oscillation at ωₙ = √(g/l) ≈ 3.13 rad/s"],
        ["Linearization at θ₀=π","A = [0, 1; +g/l, 0], eigenvalues ±√(g/l) = ±3.13\nOne positive real eigenvalue → unstable saddle"],
        ["Adding damping −bθ̇","At θ₀=0: A = [0,1; −g/l, −b/ml²], eigenvalues shift left → stable spiral\nAt θ₀=π: still one positive eigenvalue — damping cannot stabilize inverted EP"],
        ["Control at θ₀=π","Need state feedback u = −Kx to place eigenvalues in LHP\nB = [0; 1/(ml²)] — system is controllable → pole placement works"],
        ["Physical insight","sin(θ)≈θ for |θ|≲15° (0.26 rad) with <1% error\nLinear pendulum model valid for small swings only"],
      ]}/>

      <h2 style={S.h2}>Linearization Error &amp; Region of Validity</h2>
      <p style={S.p}>
        The linearized model is never exactly equal to the nonlinear system, except at the operating point itself. Quantifying the error and determining the region where the approximation is acceptable is essential for safe control design. The error grows approximately as the square of the perturbation size — doubling the perturbation roughly quadruples the error.
      </p>
      <DiagramLinearizationError/>
      <Table2 rows={[
        ["Linearization error","e(Δx) = f(x₀+Δx) − [f(x₀) + f′(x₀)·Δx]\nLeading term: ½f″(x₀)·(Δx)²  (second-order in Δx)"],
        ["Error growth rate","For f with bounded |f″| near x₀: |error| ≤ M/2·(Δx)²\nM = max |f″| in region — error is O(Δx²)"],
        ["Practical thresholds","Rule of thumb: linearisation acceptable if relative error < 5%\nFor sin(x) at x₀=0: |Δx| ≲ 0.55 rad ≈ 31°"],
        ["High-curvature functions","Functions with large |f″| near x₀ have small valid regions\nExample: 1/x near x₀ (very high curvature) vs x³ near x₀=0 (zero curvature)"],
        ["Zero second derivative","When f″(x₀)=0 (inflection point): leading error term is f‴·(Δx)³/6\nLinearisation is exceptionally accurate near inflection points"],
        ["Multiple dimensions","Error bounded by spectral norm of Hessian times ||Δx||²\nSmall perturbations in ALL state directions required simultaneously"],
        ["Validation test","Simulate nonlinear and linear models with same initial conditions\nIf step responses match well for expected operating range → linearisation valid"],
        ["Improving validity","Higher-order Taylor model (quadratic) extends valid region\nGain scheduling: use multiple local models for wider operating range"],
      ]}/>

      <h2 style={S.h2}>Gain Scheduling</h2>
      <p style={S.p}>
        When a system must operate over a wide range of conditions — an aircraft from takeoff to cruise, a chemical reactor across a broad temperature range, a robot arm over its full workspace — a single linearization is insufficient. Gain scheduling pre-designs a family of controllers, one for each operating point (or region), and schedules the appropriate controller based on a measured scheduling variable. The transitions must be smooth to avoid instability during switching.
      </p>
      <DiagramGainScheduling/>
      <Table2 rows={[
        ["Gain scheduling","Design a controller Cᵢ for each of N operating points\nAt runtime, measure scheduling variable and select/interpolate Cᵢ"],
        ["Scheduling variable","A measurable signal that indicates which operating point is active\nExamples: speed (aircraft), temperature (reactor), joint angle (robot)"],
        ["Interpolation","Between operating points, interpolate gains smoothly\nLinear interpolation, spline interpolation, or fuzzy blending"],
        ["Design at each OP","Linearize at operating point i: get Aᵢ, Bᵢ, Cᵢ, Dᵢ\nDesign controller (e.g. PID, pole-placement) for each linear model"],
        ["Aircraft example","Multiple flight conditions: takeoff, climb, cruise, descent\nDynamic pressure q=½ρv² often used as scheduling variable\nF-16 uses gain scheduled flight control across entire envelope"],
        ["Freezing assumption","Gain scheduling assumes scheduling variable changes slowly\nIf it changes rapidly, stability during transitions must be re-analysed"],
        ["LPV (Linear Parameter Varying)","Formal extension: system matrices A(ρ), B(ρ) depend on parameter ρ(t)\nLPV control provides stability guarantees during parameter variation\nMore rigorous than ad-hoc gain scheduling"],
        ["Self-scheduling","When the scheduling variable is a state or output being controlled\nMust verify stability of overall closed-loop (gain scheduling ≠ stability proof)"],
      ]}/>

      <h2 style={S.h2}>Linear vs Nonlinear Simulation</h2>
      <p style={S.p}>
        The ultimate test of a linearization is to compare the time responses of the linear and nonlinear models. When they match, the control design based on the linear model will behave as expected on the real nonlinear system. When they diverge, the linear controller may perform poorly or even destabilize the nonlinear plant.
      </p>
      <DiagramResponseComparison/>
      <Table2 rows={[
        ["Small perturbation","Linear and nonlinear responses match closely throughout\nController designed on linear model works well on nonlinear plant"],
        ["Medium perturbation","Responses diverge gradually — frequency and amplitude differ\nController may still work but with degraded performance"],
        ["Large perturbation","Significant mismatch in frequency, amplitude, and phase\nLinear controller may destabilize the nonlinear system"],
        ["Frequency shift","Nonlinear pendulum oscillates at lower frequency than linear prediction\nBecause: average of sin(θ)/θ < 1 for θ≠0"],
        ["Amplitude dependence","Nonlinear systems often exhibit amplitude-dependent frequency\nThis is called the backbone curve — absent in linear systems"],
        ["Stability margin erosion","Nonlinear effects can consume phase and gain margin\nDesign linear controller with extra margin (e.g. PM≥60°) as buffer"],
        ["Monte Carlo validation","Simulate nonlinear model from many initial conditions and perturbations\nVerify linear controller maintains acceptable performance across all cases"],
        ["Hardware-in-loop (HIL)","Test controller in real-time simulation with actual hardware\nGold standard validation before deployment on physical system"],
      ]}/>

      <h2 style={S.h2}>Linearization in Practice — Common Nonlinearities</h2>
      <Table2 rows={[
        ["Trigonometric sin/cos","sin(θ₀+Δθ) ≈ sin(θ₀) + cos(θ₀)·Δθ\nAt θ₀=0: sin(Δθ)≈Δθ (classical small-angle approximation)"],
        ["Square root √x","d/dx(√x)|ₓ₀ = 1/(2√x₀)  →  √(x₀+Δx) ≈ √x₀ + Δx/(2√x₀)\nLinear valve flow: q=√h linearises to Δq = Δh/(2√h₀)"],
        ["Product of states x₁·x₂","(x₁₀+Δx₁)(x₂₀+Δx₂) ≈ x₁₀x₂₀ + x₂₀Δx₁ + x₁₀Δx₂\nCross-product Δx₁·Δx₂ dropped (second order)"],
        ["Exponential e^x","e^(x₀+Δx) ≈ e^x₀(1+Δx)\nGrowth rate at x₀: K = e^x₀ — large for large x₀ (fast dynamics)"],
        ["Saturation","Saturating actuator: dead zone near limits\nLinearize in linear region: K=1; at saturation limit: K→0 (gain collapses)"],
        ["Coulomb friction","Non-smooth: discontinuous at zero velocity\nLinearize as viscous friction near operating velocity ẋ₀≠0\nIf ẋ₀=0: linearization fails — need piecewise or describing function analysis"],
        ["Square law (drag)","f = c·x|x| → df/dx|ₓ₀ = 2c|x₀|\nLinear drag coefficient doubles with operating speed"],
        ["Backlash / hysteresis","Non-minimum phase nonlinearity — no unique Jacobian at turning points\nDescribing function analysis used instead of linearization"],
      ]}/>

      <h2 style={S.h2}>Lyapunov Stability &amp; Linearization Limits</h2>
      <Table2 rows={[
        ["Lyapunov indirect method","If all eigenvalues of A=∂f/∂x|(x₀) have Re<0: nonlinear EP is locally asymptotically stable\nIf any eigenvalue has Re>0: nonlinear EP is unstable\nIf eigenvalues are on jω axis: inconclusive — need direct Lyapunov analysis"],
        ["Region of attraction","The set of initial conditions from which the system converges to EP\nLinearisation gives no information about size of region of attraction"],
        ["Zubov's method / SOS","Lyapunov methods can estimate region of attraction for polynomial systems\nSum-of-squares (SOS) programming provides formal certificates"],
        ["Global vs local stability","Local (linearisation): stable near EP\nGlobal: stable from all initial conditions — requires nonlinear analysis\nLinear stable ≠ globally stable"],
        ["Limit cycles","Self-sustained oscillations in nonlinear systems\nLinearisation cannot predict or analyse limit cycles\nDescribing function or Poincaré map needed"],
        ["Bifurcations","Operating-point changes that cause qualitative stability changes\nExample: Hopf bifurcation — stable spiral becomes limit cycle as parameter changes\nLinear analysis at a single OP misses these global phenomena"],
        ["Input-output linearization","Exact feedback linearization technique for nonlinear systems\nChoose nonlinear control law u=α(x)+β(x)v so that closed-loop is exactly linear\nMore powerful than Taylor linearization — valid globally (in some cases)"],
        ["Feedback linearization","u = [−f(x) + v] / g(x) transforms ẋ=f(x)+g(x)u to ẋ=v (linear)\nRequires exact knowledge of f and g — sensitive to modelling errors"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Single variable: f(x) ≈ f(x₀) + f′(x₀)·Δx   where Δx = x − x₀",
        "State equation Jacobian:  A = ∂f/∂x|(x₀,u₀),  B = ∂f/∂u|(x₀,u₀)",
        "Output equation Jacobian: C = ∂g/∂x|(x₀,u₀),  D = ∂g/∂u|(x₀,u₀)",
        "Linearised state-space: Δẋ = A·Δx + B·Δu,  Δy = C·Δx + D·Δu",
        "Transfer function from SS: G(s) = C(sI−A)⁻¹B + D",
        "Stability test: all eigenvalues of A must have Re(λ) < 0",
        "Taylor remainder bound: |error| ≤ max|f″|/2 · (Δx)²",
        "Pendulum at θ₀=0: A=[0,1;−g/l,0] → ωₙ=√(g/l);  at θ₀=π: A=[0,1;+g/l,0] → unstable",
      ]}/>
    </div>
  );
}